import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../../config/config.js";
import { useAuth } from "../../login/AuthContext";
import { toast } from "react-toastify";
import "../../customers/customers.css";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { copyToClipboard } from "../../../utils/copyToClipboard.js";

function durumChip(row) {
  const label =
    row.DurumLabel ||
    (row.SuresiDoldu ? "Süresi Doldu" : row.AktifMi ? "Aktif" : "Pasif");
  const color =
    label === "Aktif" ? "success" : label === "Süresi Doldu" ? "error" : "default";
  return <Chip size="small" label={label} color={color} />;
}

function formatMusteriTelefon(row) {
  const parts = [row.MusteriTel, row.MusteriTel2].filter(
    (v) => v != null && String(v).trim() !== "",
  );
  return parts.length ? parts.join(" / ") : "-";
}

export default function GastoraLicenseList() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(
    async (search = "") => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ limit: "200" });
        if (search.trim()) params.set("search", search.trim());
        const res = await axios.get(
          `${config.baseURL}/gastora-lisanslar?${params.toString()}`,
          { headers },
        );
        setRows(res.data?.data || []);
      } catch {
        toast.error("Gastro Lisansları yüklenemedi");
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchData("");
  }, [fetchData]);

  const handleSearch = (value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchData(value);
    }, 300);
  };

  const copyKey = async (key) => {
    const ok = await copyToClipboard(key);
    if (ok) {
      toast.success("Lisans anahtarı kopyalandı");
    } else {
      toast.error("Kopyalanamadı — anahtarı elle seçip Ctrl+C deneyin");
    }
  };

  const setEtkin = async (id, etkin) => {
    try {
      await axios.patch(
        `${config.baseURL}/gastora-lisanslar/${id}/etkin`,
        { Etkin: etkin },
        { headers },
      );
      toast.success(etkin ? "Lisans aktif edildi" : "Lisans pasife alındı");
      fetchData(searchText);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Durum güncellenemedi");
    }
  };

  const renewToken = async (id) => {
    try {
      const res = await axios.put(
        `${config.baseURL}/gastora-lisanslar/${id}/token-yenile`,
        {},
        { headers },
      );
      toast.success("QR Menü token yenilendi");
      if (res.data?.CloudQrToken) {
        const copied = await copyToClipboard(res.data.CloudQrToken);
        if (copied) {
          toast.info("Yeni token panoya kopyalandı");
        }
      }
      fetchData(searchText);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Token yenilenemedi");
    }
  };

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Gastro Lisanslar</h1>
      </div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Süresi dolan lisanslar otomatik pasife düşer. Manuel pasif olanlar tekrar
        aktif edilebilir; süresi dolmuş lisanslar için yenileme gerekir.
      </Typography>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Lisans key, müşteri, telefon, paket..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <CircularProgress sx={{ m: 4 }} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lisans Key</TableCell>
                <TableCell>Müşteri</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Paket</TableCell>
                <TableCell>Başlangıç</TableCell>
                <TableCell>Bitiş</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Kurulum</TableCell>
                <TableCell>Kullanıcı</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Modüller</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.Id}>
                  <TableCell>
                    <code>{r.LisansKey}</code>
                    <IconButton size="small" onClick={() => copyKey(r.LisansKey)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell>{r.MusteriUnvan || r.MusteriId}</TableCell>
                  <TableCell>{formatMusteriTelefon(r)}</TableCell>
                  <TableCell>{r.PaketAdi || "-"}</TableCell>
                  <TableCell>
                    {r.BaslangicTarih
                      ? new Date(r.BaslangicTarih).toLocaleDateString("tr-TR")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {r.BitisTarih
                      ? new Date(r.BitisTarih).toLocaleDateString("tr-TR")
                      : "-"}
                  </TableCell>
                  <TableCell>{r.LisansTipi}</TableCell>
                  <TableCell>
                    {String(r.KurulumTipi || "").toUpperCase() === "CLOUD"
                      ? "Bulut"
                      : "Yerel"}
                  </TableCell>
                  <TableCell>{r.MaxKullaniciSayisi ?? "-"}</TableCell>
                  <TableCell>{durumChip(r)}</TableCell>
                  <TableCell>
                    {[
                      r.moduleQrMenu && "QR",
                      r.moduleRestoran && "Restoran",
                      r.modulePaketServis && "Paket",
                      r.moduleUretim && "Üretim",
                      ...((r.PurchasedEkModuller || []).map((m) => m.Adi || m.ProgramAdi)),
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      flexWrap="wrap"
                    >
                      {r.PasifEdilebilir && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setEtkin(r.Id, false)}
                        >
                          Pasife Al
                        </Button>
                      )}
                      {r.AktifEdilebilir && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => setEtkin(r.Id, true)}
                        >
                          Aktife Al
                        </Button>
                      )}
                      {r.moduleQrMenu && (
                        <Tooltip title="QR Menü local bağlantı tokeni">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => renewToken(r.Id)}
                          >
                            Token Yenile
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Kayıt bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
